import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatINR, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ChevronRight, Package, RefreshCw, X } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { ProductImage } from "../components/ProductImage";

const STATUS_LABEL = {
  placed: "Order Placed",
  packed: "Packed",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",
};
const STATUS_COLOR = {
  placed: "bg-blue-100 text-blue-800",
  packed: "bg-amber-100 text-amber-800",
  out_for_delivery: "bg-stone-200 text-stone-900",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

const REASONS = [
  "Ordered by mistake",
  "Changed my mind",
  "Delivery taking too long",
  "Found another option",
  "Other",
];

function CancelDialog({ order, onClose, onDone }) {
  const [reason, setReason] = useState(REASONS[0]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}/cancel`, { reason });
      toast.success("Order cancelled");
      onDone();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="cancel-dialog">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Cancel this order?</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 hover:bg-stone-100"
            data-testid="cancel-dialog-close"
            aria-label="Close cancellation dialog"
          >
            <X size={16} />
          </button>
        </div>
        <p className="mt-1 text-xs text-stone-500">Please tell us why so we can improve.</p>
        <div className="mt-3 space-y-1">
          {REASONS.map((r) => (
            <label key={r} className="flex items-center gap-2 rounded-lg border border-stone-200 px-3 py-2 text-sm hover:bg-stone-50">
              <input
                type="radio"
                name="reason"
                checked={reason === r}
                onChange={() => setReason(r)}
                data-testid={`cancel-reason-${r.toLowerCase().replaceAll(" ", "-")}`}
              />
              {r}
            </label>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-full border border-stone-300 py-2 text-sm"
            data-testid="cancel-dialog-dismiss"
          >
            Keep order
          </button>
          <button
            data-testid="confirm-cancel"
            disabled={busy}
            onClick={submit}
            className="flex-1 rounded-full bg-red-600 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy ? "Cancelling..." : "Cancel order"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [dialog, setDialog] = useState(null);
  const nav = useNavigate();
  const { add, setOpen } = useCart();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/orders/mine");
      setOrders(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const reorder = async (o) => {
    let added = 0;
    for (const it of o.items) {
      try {
        const { data: p } = await api.get(`/products/${it.product_id}`);
        const variant = it.variant_id
          ? (p.variants || []).find((v) => v.id === it.variant_id) || null
          : null;
        for (let i = 0; i < it.qty; i++) add(p, variant);
        added += it.qty;
      } catch {
        toast.error(`${it.name} not available anymore — skipped`);
      }
    }
    if (added > 0) {
      toast.success(`Added ${added} items back to your cart`);
      setOpen(true);
    }
  };

  const downloadInvoice = async (oid, orderNo) => {
    try {
      const res = await api.get(`/orders/${oid}/invoice`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const filename = `${orderNo || oid.slice(0, 8).toUpperCase()}.pdf`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Mobile browsers that ignore the download attribute still open the PDF.
      setTimeout(() => {
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }, 300);
    } catch {
      toast.error("Could not open the invoice. Please try again.");
    }
  };

  return (
    <main className="mx-auto max-w-4xl px-4 pb-28 pt-8 md:px-6 md:pb-12">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-orange-700">Order history</p>
      <h1 className="mt-2 font-serif text-4xl tracking-tight text-stone-950">My Orders</h1>
      <p className="mt-2 text-sm text-stone-500">Track current orders and revisit previous ones.</p>

      <div className="mt-6 space-y-3">
        {loading && (
          <div className="space-y-3" data-testid="orders-loading">
            {[1, 2, 3].map((placeholder) => (
              <div key={placeholder} className="h-28 animate-pulse border border-stone-100 bg-stone-100" />
            ))}
          </div>
        )}
        {!loading && error && (
          <div className="border border-red-200 bg-red-50 p-5 text-sm text-red-800" data-testid="orders-error">
            {error}
          </div>
        )}
        {!loading && !error && orders.length === 0 && (
          <div
            className="border border-dashed border-stone-300 bg-white p-12 text-center"
            data-testid="orders-empty"
          >
            <Package className="mx-auto h-9 w-9 text-orange-600" strokeWidth={1.5} />
            <p className="font-serif text-2xl">No orders yet</p>
            <Link
              to="/c/all"
              className="mt-4 inline-block min-h-11 bg-orange-600 px-4 py-3 text-sm font-semibold text-white"
              data-testid="orders-start-shopping"
            >
              Start shopping
            </Link>
          </div>
        )}
        {!loading && !error && orders.map((o) => {
          const canCancel = o.status === "placed" || o.status === "packed";
          return (
            <div
              key={o.id}
              data-testid={`order-row-${o.id}`}
              className="border border-stone-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-center gap-4">
                <div className="flex -space-x-2">
                  {o.items.slice(0, 3).map((i, k) => (
                    <ProductImage
                      key={`${i.product_id}-${k}`}
                      src={i.image}
                      alt={i.name}
                      className="h-12 w-12 rounded-full border-2 border-white object-cover"
                      testId={`order-product-image-${o.id}-${k}`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  className="min-w-0 flex-1 cursor-pointer text-left"
                  onClick={() => nav(`/orders/${o.id}`)}
                  data-testid={`order-details-${o.id}`}
                >
                  <p className="truncate text-sm font-semibold">
                    {o.items.map((i) => (i.variant_label ? `${i.name} (${i.variant_label})` : i.name)).join(", ")}
                  </p>
                  <p className="text-xs text-stone-500 font-mono">
                    {o.order_no || `#${o.id.slice(0, 8).toUpperCase()}`} · {new Date(o.created_at).toLocaleString()}
                  </p>
                </button>
                <div className="text-right">
                  <span
                    data-testid={`order-status-${o.id}`}
                    className={`rounded-full px-2 py-1 text-[10px] font-semibold ${STATUS_COLOR[o.status]}`}
                  >
                    {STATUS_LABEL[o.status]}
                  </span>
                  <p className="mt-1 text-sm font-semibold" data-testid={`order-total-${o.id}`}>
                    {formatINR(o.total)}
                  </p>
                  <p className="mt-1 text-xs text-sky-700" data-testid={`order-eta-${o.id}`}>
                    ETA: {o.eta_label || "Will be updated shortly"}
                  </p>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Link
                  to={`/orders/${o.id}`}
                  className="inline-flex min-h-9 items-center gap-1 border border-stone-300 px-3 text-xs font-semibold text-stone-800 hover:bg-stone-50"
                  data-testid={`track-order-${o.id}`}
                >
                  Track order <ChevronRight className="h-3 w-3" />
                </Link>
                <button
                  data-testid={`reorder-${o.id}`}
                  onClick={() => reorder(o)}
                  className="inline-flex items-center gap-1 rounded-full bg-orange-600 px-3 py-1 text-xs font-semibold text-white hover:bg-orange-700"
                >
                  <RefreshCw size={11} /> Reorder
                </button>
                <button
                  data-testid={`invoice-${o.id}`}
                  onClick={() => downloadInvoice(o.id, o.invoice_no || o.order_no)}
                  className="inline-flex items-center gap-1 rounded-full border border-stone-300 px-3 py-1 text-xs font-semibold text-stone-800 hover:bg-stone-50"
                >
                  Invoice
                </button>
                {canCancel && (
                  <button
                    data-testid={`cancel-order-${o.id}`}
                    onClick={() => setDialog(o)}
                    className="rounded-full border border-red-300 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50"
                  >
                    Cancel order
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {dialog && (
        <CancelDialog order={dialog} onClose={() => setDialog(null)} onDone={load} />
      )}
    </main>
  );
}
